package com.evoapps.evoshift.data.remote

import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.DocumentSnapshot
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.firestore.Query
import dev.gitlive.firebase.firestore.QuerySnapshot
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch

/**
 * A live query or document that never takes the app down. When the server refuses or ends a
 * listener (most often PERMISSION_DENIED: a rule the caller does not meet, e.g. right after
 * losing a role), the Firestore error arrives in the collector's coroutine. On iOS an uncaught
 * exception there terminates the app: 1.0.23 crashed on the owner's dashboard this way. Here the
 * listener ends instead, the error is reported to [ListenerErrors], and the screen keeps what it
 * had. Code that turns a refusal into an empty result (`catch { emit(emptyList()) }`) keeps using
 * `snapshots` directly.
 */
val Query.safeSnapshots: Flow<QuerySnapshot>
    get() = snapshots.catch { e -> if (e is FirebaseFirestoreException) ListenerErrors.report(e) else throw e }

/** [Query.safeSnapshots] for one document. */
val DocumentReference.safeSnapshots: Flow<DocumentSnapshot>
    get() = snapshots.catch { e -> if (e is FirebaseFirestoreException) ListenerErrors.report(e) else throw e }

/** Where ended listeners are reported (the app wires it to crash reporting as a non-fatal). */
object ListenerErrors {
    var reporter: (Throwable) -> Unit = { e -> println("Firestore listener ended: ${e.message}") }
    fun report(e: Throwable) = runCatching { reporter(e) }.let { }
}

/**
 * Whether the server refused the request under the security rules. Read the code, not the text:
 * Android says "PERMISSION_DENIED: …", iOS "Missing or insufficient permissions." (1.0.23 crashed
 * on iOS because a check matched only the Android text and rethrew).
 */
fun Throwable.isPermissionDenied(): Boolean {
    if ((this as? FirebaseFirestoreException)?.code == dev.gitlive.firebase.firestore.FirestoreExceptionCode.PERMISSION_DENIED) return true
    val text = message.orEmpty()
    return listOf("PERMISSION_DENIED", "permission-denied", "Missing or insufficient permissions").any { it in text }
}
